"use client";

import { useCallback, useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  ColumnPicker,
  InputSettings,
  OUTPUT_DELIMITERS,
  type OutputDelimiter,
  OutputSettings,
  RowSettings,
  Warnings,
} from "@/components/tools/text/column-extractor-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  columnCount,
  extractColumns,
  type InputFormat,
  parseColumnSpec,
  parseInput,
  type SortDirection,
} from "@/lib/tool-kit/text/column-extractor";
import { type QuoteMode, toDelimited } from "@/lib/tool-kit/text/delimited";

const PREVIEW_ROWS = 20;

/** English strings. A translated content file overrides these through `ui`. */
export const COLUMN_EXTRACTOR_STRINGS = {
  inputLabel: "Your data",
  inputPlaceholder:
    "Paste CSV, TSV or any delimited text — one row per line, the header row first",
  trySample: "Try a sample",
  sample:
    'Student,Age,Country,Study spot,Hours per week\nAmara,19,Kenya,"Library, 3rd floor",12\nBen,22,Canada,"Café ""The Grind""",8\nChloé,20,France,Library,15\nDiego,21,Mexico,Home,10\nElif,19,Canada,"Library, 3rd floor",12',
  sampleSpec: "Student, Hours per week, Country",
  clear: "Clear",
  formatLabel: "Input format",
  formats: {
    auto: "Detect automatically",
    comma: "Comma-separated (CSV)",
    tab: "Tab-separated (TSV)",
    semicolon: "Semicolon-separated",
    pipe: "Pipe-separated ( | )",
    custom: "Custom delimiter…",
    whitespace: "Spaces and tabs",
    fixed: "Fixed-width columns",
  } as Record<InputFormat, string>,
  customDelimiterLabel: "Delimiter",
  fixedStartsLabel: "Column start positions",
  fixedStartsHint:
    "Character positions where each column begins, counting from 1. Leave empty to find them from the blank columns that line up in every row.",
  detectStarts: "Find columns",
  hasHeader: "First row is a header",
  detected: "Read as {format}: {rows} and {columns}.",
  detectedDelimiter: "“{delimiter}”-separated text",
  rowCount: "one: {count} row|other: {count} rows",
  columnCountLabel: "one: {count} column|other: {count} columns",
  columnsFound: "Columns found — select one to add it",
  columnChip: "{number} · {name}",
  addColumn: "Add column {number}",
  specLabel: "Columns to keep, in order",
  specPlaceholder: "e.g. 3, 1, 5-7, Email",
  specHint:
    "Column numbers, ranges such as 2-4 (or 4-2 to reverse, 3- to the end), * for all, or header names. Separate with commas; put a name in quotes if it contains a comma. Leave empty to keep every column.",
  unknownColumns: "Not found, so left out: {list}",
  renameHeading: "Rename columns (optional)",
  renameLabel: "New name for {name}",
  columnName: "Column {number}",
  trimCells: "Trim spaces around values",
  skipEmptyRows: "Skip rows that are empty in the chosen columns",
  dedupe: "Remove duplicate rows",
  sortLabel: "Sort rows by",
  noSort: "Don't sort — keep the input order",
  orderLabel: "Order",
  orders: {
    asc: "A to Z, 0 to 9",
    desc: "Z to A, 9 to 0",
  } as Record<SortDirection, string>,
  outputDelimiterLabel: "Output delimiter",
  outputDelimiters: {
    comma: "Comma (CSV)",
    tab: "Tab (paste into a spreadsheet)",
    semicolon: "Semicolon",
    pipe: "Pipe ( | )",
    space: "Space",
    custom: "Custom…",
  } as Record<OutputDelimiter, string>,
  outputCustomLabel: "Output delimiter text",
  quoteLabel: "Quote values",
  quotes: {
    needed: "Only when needed (standard CSV)",
    always: "Always",
    never: "Never",
  } as Record<QuoteMode, string>,
  includeHeader: "Include the header row in the output",
  results: "Extracted columns",
  emptyPrompt: "Paste some delimited text above to pick columns from it.",
  noColumns: "Choose at least one column to extract.",
  summary: "{rows} × {columns}",
  previewNote:
    "Showing the first {count} rows. The copy and the download include them all.",
  unterminatedQuote:
    "A quoted value is never closed, so everything after its opening quote was read as one value. Check for a missing quotation mark.",
  strayQuotes:
    "one: {count} row has text after a closing quotation mark; it was kept as part of the value.|other: {count} rows have text after a closing quotation mark; it was kept as part of the value.",
  shortRows:
    "one: {count} row is missing some of the chosen columns; those cells are left blank.|other: {count} rows are missing some of the chosen columns; those cells are left blank.",
  duplicatesRemoved:
    "one: {count} duplicate row removed.|other: {count} duplicate rows removed.",
  emptyRowsSkipped:
    "one: {count} empty row skipped.|other: {count} empty rows skipped.",
  outputLabel: "Output",
  copy: "Copy",
  copied: "Copied!",
  download: "Download",
  filenames: {
    csv: "extracted-columns.csv",
    tsv: "extracted-columns.tsv",
    txt: "extracted-columns.txt",
  },
} as const;

export type ColumnExtractorStrings = typeof COLUMN_EXTRACTOR_STRINGS;

/** "1, 15, 21" (1-based, as typed) → [0, 14, 20]. */
function fixedStarts(text: string): number[] {
  return parseNumberList(text)
    .filter((value) => Number.isInteger(value) && value >= 1)
    .map((value) => value - 1);
}

function filenameFor(
  delimiter: OutputDelimiter,
  strings: ColumnExtractorStrings
): string {
  if (delimiter === "comma") {
    return strings.filenames.csv;
  }
  return delimiter === "tab" ? strings.filenames.tsv : strings.filenames.txt;
}

export default function ColumnExtractorWidget() {
  const strings = useToolUi(COLUMN_EXTRACTOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [format, setFormat] = useState<InputFormat>("auto");
  const [customDelimiter, setCustomDelimiter] = useState(" | ");
  const [startsText, setStartsText] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [spec, setSpec] = useState("");
  const [renames, setRenames] = useState<string[]>([]);
  const [trimCells, setTrimCells] = useState(true);
  const [skipEmptyRows, setSkipEmptyRows] = useState(true);
  const [dedupe, setDedupe] = useState(false);
  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [outputDelimiter, setOutputDelimiter] =
    useState<OutputDelimiter>("comma");
  const [outputCustom, setOutputCustom] = useState(" | ");
  const [quoteMode, setQuoteMode] = useState<QuoteMode>("needed");
  const [includeHeader, setIncludeHeader] = useState(true);
  const text = useDeferredValue(input);

  const parsed = useMemo(
    () =>
      parseInput(text, {
        format,
        customDelimiter,
        fixedStarts: fixedStarts(startsText),
      }),
    [text, format, customDelimiter, startsText]
  );
  const count = columnCount(parsed.rows);
  const headers = hasHeader ? (parsed.rows[0] ?? null) : null;
  const resolved = useMemo(
    () => parseColumnSpec(spec, headers, count, locale),
    [spec, headers, count, locale]
  );
  const columnLabel = useCallback(
    (column: number) => t(strings.columnName, { number: fmt(column + 1) }),
    [strings, fmt]
  );
  const extraction = useMemo(
    () =>
      extractColumns(parsed.rows, {
        hasHeader,
        columns: resolved.columns,
        renames,
        fallbackName: columnLabel,
        trimCells,
        skipEmptyRows,
        dedupe,
        sortBy: sortBy === "none" ? -1 : Number(sortBy),
        sortDirection,
        locale,
      }),
    [
      parsed,
      hasHeader,
      resolved,
      renames,
      columnLabel,
      trimCells,
      skipEmptyRows,
      dedupe,
      sortBy,
      sortDirection,
      locale,
    ]
  );
  const delimiter =
    outputDelimiter === "custom"
      ? outputCustom
      : OUTPUT_DELIMITERS[outputDelimiter];
  const outputRows = [
    ...(includeHeader && extraction.header ? [extraction.header] : []),
    ...extraction.rows,
  ];
  const output = toDelimited(outputRows, delimiter, quoteMode);
  const outputColumns = resolved.columns.map(
    (column, position) =>
      renames[position]?.trim() ||
      headers?.[column]?.trim() ||
      columnLabel(column)
  );

  return (
    <>
      <TextAreaField
        id="colx-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        rows={8}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setInput(strings.sample);
            setFormat("auto");
            setHasHeader(true);
            setSpec(strings.sampleSpec);
            setRenames([]);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <InputSettings
        customDelimiter={customDelimiter}
        format={format}
        hasHeader={hasHeader}
        onCustomDelimiter={setCustomDelimiter}
        onDetectStarts={() =>
          setStartsText(
            parseInput(text, {
              format: "fixed",
              customDelimiter,
              fixedStarts: [],
            })
              .fixedStarts.map((start) => start + 1)
              .join(", ")
          )
        }
        onFormat={setFormat}
        onHasHeader={setHasHeader}
        onStartsText={setStartsText}
        parsed={parsed}
        startsText={startsText}
        strings={strings}
      />
      {count > 0 ? (
        <ColumnPicker
          count={count}
          headers={headers}
          onAdd={(column) =>
            setSpec((current) =>
              current.trim() ? `${current.trim()}, ${column}` : String(column)
            )
          }
          onRename={(position, name) =>
            setRenames((current) => {
              const next = [...current];
              next[position] = name;
              return next;
            })
          }
          onSpec={setSpec}
          outputColumns={outputColumns}
          renames={renames}
          resolved={resolved}
          spec={spec}
          strings={strings}
        />
      ) : null}
      <RowSettings
        dedupe={dedupe}
        onDedupe={setDedupe}
        onSkipEmptyRows={setSkipEmptyRows}
        onSortBy={setSortBy}
        onSortDirection={setSortDirection}
        onTrimCells={setTrimCells}
        outputColumns={outputColumns}
        skipEmptyRows={skipEmptyRows}
        sortBy={sortBy}
        sortDirection={sortDirection}
        strings={strings}
        trimCells={trimCells}
      />
      <OutputSettings
        includeHeader={includeHeader}
        onIncludeHeader={setIncludeHeader}
        onOutputCustom={setOutputCustom}
        onOutputDelimiter={setOutputDelimiter}
        onQuoteMode={setQuoteMode}
        outputCustom={outputCustom}
        outputDelimiter={outputDelimiter}
        quoteMode={quoteMode}
        strings={strings}
      />
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {parsed.rows.length === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : null}
        {parsed.rows.length > 0 && resolved.columns.length === 0 ? (
          <Notice>{strings.noColumns}</Notice>
        ) : null}
        {parsed.rows.length > 0 && resolved.columns.length > 0 ? (
          <>
            <p className="text-foreground text-sm">
              {t(strings.summary, {
                rows: tPlural(
                  strings.rowCount,
                  locale,
                  extraction.rows.length,
                  {
                    count: fmt(extraction.rows.length),
                  }
                ),
                columns: tPlural(
                  strings.columnCountLabel,
                  locale,
                  resolved.columns.length,
                  { count: fmt(resolved.columns.length) }
                ),
              })}
            </p>
            <Warnings
              extraction={extraction}
              parsed={parsed}
              strings={strings}
            />
            <DataTable
              head={extraction.header ?? outputColumns}
              rows={extraction.rows.slice(0, PREVIEW_ROWS)}
            />
            {extraction.rows.length > PREVIEW_ROWS ? (
              <p className={HINT}>
                {t(strings.previewNote, { count: fmt(PREVIEW_ROWS) })}
              </p>
            ) : null}
            <label className={LABEL} htmlFor="colx-output">
              {strings.outputLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="colx-output"
              readOnly
              value={output}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    filenameFor(outputDelimiter, strings),
                    `${output}\n`,
                    outputDelimiter === "comma"
                      ? "text/csv;charset=utf-8"
                      : "text/plain;charset=utf-8"
                  )
                }
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
